import type { ImportStudyConfig } from './importStudyConfig';
import { DEFAULT_IMPORT_STUDY_CONFIG } from './importStudyConfig';
import { IMPORT_STUDY_MESSAGES } from './importStudyMessages';

export type ImportStudyFile = File;

export type ImportStudyValidationSuccess = {
  valid: true;
  files: ImportStudyFile[];
};

export type ImportStudyValidationFailure = {
  valid: false;
  message: string;
};

export type ImportStudyValidationResult =
  | ImportStudyValidationSuccess
  | ImportStudyValidationFailure;

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) {
    return '';
  }

  return fileName.slice(dotIndex).toLowerCase();
}

export function isImportStudyZipFile(file: ImportStudyFile): boolean {
  return getFileExtension(file.name) === '.zip';
}

export function isImportStudyDicomFile(file: ImportStudyFile): boolean {
  const extension = getFileExtension(file.name);

  if (extension === '.dcm' || extension === '.dicom') {
    return true;
  }

  return !extension && file.type === 'application/dicom';
}

function isSupportedImportStudyFile(file: ImportStudyFile): boolean {
  return isImportStudyDicomFile(file) || isImportStudyZipFile(file);
}

export async function validateImportStudyFiles(
  files: ImportStudyFile[],
  config: ImportStudyConfig = DEFAULT_IMPORT_STUDY_CONFIG
): Promise<ImportStudyValidationResult> {
  if (!files.length) {
    return { valid: false, message: '' };
  }

  if (files.some(file => !isSupportedImportStudyFile(file))) {
    return { valid: false, message: IMPORT_STUDY_MESSAGES.UNSUPPORTED_FORMAT };
  }

  if (files.some(file => file.size > config.maxUploadSizeBytes)) {
    return { valid: false, message: IMPORT_STUDY_MESSAGES.FILE_TOO_LARGE };
  }

  return { valid: true, files };
}
